/**
 *
 * loginWithFirebase
 * Get the API session for a signed-in Firebase user: the API verifies the
 * Firebase ID token and takes the email address from it.
 *
 */

import { call } from 'redux-saga/effects';
import request from 'utils/request';
import sleep from 'utils/sleep';
import { API2_URL } from 'config/constants';

// Waits before each new attempt after a network error or a 5xx, e.g. while a
// phone reconnects after resuming a tab, or while the API restarts
export const RETRY_DELAYS = [1000, 3000, 10000];

// fetch has no timeout of its own: a stalled request would keep the spinner up for good
export const REQUEST_TIMEOUT = 15000;

export function statusOf(error) {
  return error?.response?.status;
}

function isTemporary(error) {
  const status = statusOf(error);
  return !status || status >= 500;
}

function* requestSession(firebaseUser, forceRefresh) {
  const idToken = yield call(
    [firebaseUser, firebaseUser.getIdToken],
    forceRefresh,
  );
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  try {
    // An aborted request has no response status, so it counts as temporary
    return yield call(request, `${API2_URL}/user/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
      signal: controller.signal,
      // Retries are handled here, with longer waits than retryFetch uses
      retry: false,
    });
  } finally {
    clearTimeout(timer);
  }
}

export default function* loginWithFirebase(firebaseUser) {
  let forceRefresh = false;
  let attempt = 0;
  for (;;) {
    try {
      return yield call(requestSession, firebaseUser, forceRefresh);
    } catch (error) {
      // A 401 can mean the cached ID token just expired: try once with a fresh one
      if (statusOf(error) === 401 && !forceRefresh) {
        forceRefresh = true;
      } else if (isTemporary(error) && attempt < RETRY_DELAYS.length) {
        yield call(sleep, RETRY_DELAYS[attempt]);
        attempt += 1;
      } else {
        throw error;
      }
    }
  }
}
